import IconButtonComponent from '../../../IconButton.component';
import ShareIcon from '../../../Icons/Share.icon';
import CandidateInfoBlockComponent from '../../../Blocks/CandidateInfoBlock.component';
import { Dispatch, FC, SetStateAction } from 'react';
import styled, { css } from 'styled-components';
import { StyledBaseIcon } from '../../../Icons/Base.icon';
import { Person } from '../../../../redux/Candidate/Candidate.type';
import { getUrlBasedOnCurrentPath } from '../../Candidate/utils';
import { RoutesConstant } from '../../../../constants/routes.constant';
import { CANDIDATES_GROUPS } from '../../../../types/candidates.types';
import { useLocation } from 'react-router-dom';
import CandidateDuplicateList from './CandidateDuplicateList';
import { useTranslation } from 'react-i18next';

interface CandidateContentProps {
  candidates: Person[];
  selectedCandidate: number;
  setSelectedCandidate: Dispatch<SetStateAction<number>>;
}

const StyledCandidateContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
`;

const StyledCandidateContentListWrapper = styled.div<{
  onlyOneCandidate: boolean;
}>`
  ${props => props.onlyOneCandidate && StyledOnlyOneDuplicatedCandidate};

  max-height: 280px;
  overflow: auto;
`;

const StyledOnlyOneDuplicatedCandidate = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledCandidateContentColumns = styled.div`
  display: flex;
  align-items: center;
  min-width: 350px;
  padding: 0 10px;
`;

const StyledCandidateContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 0 0 10px;
`;

const StyledCandidateContentTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${StyledBaseIcon} {
    color: ${({ theme }) => theme.colors.BLUE.B400};
  }
`;

const StyledCandidateContentEllipsis = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 250px;
`;

const StyledCandidateContentBlock = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.TITLE};

  ${StyledCandidateContentEllipsis}
`;

const CandidateContent: FC<CandidateContentProps> = ({
  candidates,
  setSelectedCandidate,
  selectedCandidate,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const candidate = candidates.find(
    candidate => candidate.id === selectedCandidate,
  );

  const candidateLink = getUrlBasedOnCurrentPath(
    location,
    candidate?.id || 0, // ! я так понимаю 0 тут значит "несуществующий/новый" кандидат. шарим несуществующего кандидата? (вежливый вопрос по незнанию)
    RoutesConstant.CANDIDATES_GROUP,
    CANDIDATES_GROUPS.ALL,
  );

  const links = candidate && candidate.links ? candidate.links : null;
  const experience =
    candidate && candidate.experience ? candidate.experience : null;
  const email = candidate && candidate.email ? candidate.email : null;
  const phone = candidate && candidate.phone ? candidate.phone : null;
  const tags = candidate && candidate.tags ? candidate.tags : null;
  const metaRegions =
    candidate && candidate.metaRegions ? candidate.metaRegions : null;
  const english = candidate && candidate.english ? candidate.english : null;
  const hasUnreachableRegions = candidate && candidate.hasUnreachableRegions ? candidate.hasUnreachableRegions : null;


  const isLinkedin = window.location.origin.includes('linkedin');

  let linkedinHunterUrl = null;

  if (isLinkedin) {
    linkedinHunterUrl = localStorage.getItem('hunterUrl');
  }

  return (
    <StyledCandidateContentWrapper>
      <StyledCandidateContentColumns>
        <StyledCandidateContentColumn>
          <StyledCandidateContentTitle>
            <StyledCandidateContentBlock>
              {candidate?.fullName || t('personDuplicate.notSelected')}
            </StyledCandidateContentBlock>
            {!!selectedCandidate && (
              <IconButtonComponent link={linkedinHunterUrl ? `${linkedinHunterUrl}${candidateLink}`: candidateLink}>
                <ShareIcon />
              </IconButtonComponent>
            )}
          </StyledCandidateContentTitle>
          <CandidateInfoBlockComponent
            links={links}
            experience={experience}
            email={email}
            phone={phone}
            tags={tags}
            metaRegions={metaRegions}
            english={english}
            hasUnreachableRegions={hasUnreachableRegions}
            hasUnreachableRegionsVarning={true}
          />
        </StyledCandidateContentColumn>
      </StyledCandidateContentColumns>

      <StyledCandidateContentListWrapper
        onlyOneCandidate={candidates.length === 1}
      >
        {candidates.map(({ fullName, email, phone, id }) => (
          <CandidateDuplicateList
            key={id}
            fullName={fullName}
            email={email}
            phone={phone}
            id={id}
            selectedCandidate={selectedCandidate}
            setSelectedCandidate={setSelectedCandidate}
          />
        ))}
      </StyledCandidateContentListWrapper>
    </StyledCandidateContentWrapper>
  );
};

export default CandidateContent;
